import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, SkipBack, SkipForward, Microphone, MicrophoneSlash, ChatCircle, SpeakerHigh, SpeakerSlash, Timer, Ear } from "@phosphor-icons/react";
import { getRecipe, getRecipeSteps, askCoach } from "../lib/api";

function speak(text, onEnd) {
  if (!("speechSynthesis" in window)) { onEnd && onEnd(); return null; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.95; u.pitch = 1.0;
  if (onEnd) u.onend = onEnd;
  window.speechSynthesis.speak(u);
  return u;
}

export default function VoiceCook() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [answer, setAnswer] = useState("");
  const [answerLoading, setAnswerLoading] = useState(false);
  const [timerLeft, setTimerLeft] = useState(0);      // seconds remaining
  const [timerRunning, setTimerRunning] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [wakeActive, setWakeActive] = useState(false);
  const recogRef = useRef(null);
  const wakeRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [r, s] = await Promise.all([getRecipe(id), getRecipeSteps(id)]);
        setRecipe(r); setSteps(s.steps || []);
      } catch (e) { toast.error("Failed to load"); }
      finally { setLoading(false); }
    })();
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (wakeRef.current) { try { wakeRef.current.stop(); } catch (_) {} wakeRef.current = null; }
    };
  }, [id]);

  const speakStep = useCallback((i) => {
    if (muted) return;
    const step = steps[i];
    if (!step) return;
    setPlaying(true);
    speak(step.text, () => setPlaying(false));
  }, [steps, muted]);

  // Timer effect: ticks every second while running
  useEffect(() => {
    if (!timerRunning) { if (tickRef.current) clearInterval(tickRef.current); return; }
    tickRef.current = setInterval(() => {
      setTimerLeft(t => {
        if (t <= 1) {
          clearInterval(tickRef.current);
          setTimerRunning(false);
          // auto-advance
          if (autoAdvance) {
            setIdx(cur => {
              const nxt = Math.min(steps.length - 1, cur + 1);
              // will trigger a fresh speakStep via startTimer below
              return nxt;
            });
          } else {
            // announce ding
            if (!muted) speak("Timer complete.");
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [timerRunning, autoAdvance, steps.length, muted]);

  // When idx changes AND autoAdvance came from a timer complete, speak next step + start its timer
  useEffect(() => {
    // narrate current step whenever idx changes (after initial load)
    if (steps.length === 0) return;
    if (!muted) speak(steps[idx]?.text || "", () => setPlaying(false));
    setPlaying(true);
    const dur = Number(steps[idx]?.duration_sec || 0);
    if (dur > 0 && autoAdvance) {
      setTimerLeft(dur); setTimerRunning(true);
    } else {
      setTimerLeft(0); setTimerRunning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, steps]);

  const startTimer = () => {
    const dur = Number(steps[idx]?.duration_sec || 0);
    if (dur <= 0) return;
    setTimerLeft(dur); setTimerRunning(true);
  };
  const pauseTimer = () => setTimerRunning(false);
  const resetTimer = () => { setTimerRunning(false); setTimerLeft(Number(steps[idx]?.duration_sec || 0)); };

  const next = () => { const n = Math.min(steps.length - 1, idx + 1); setIdx(n); speakStep(n); };
  const prev = () => { const n = Math.max(0, idx - 1); setIdx(n); speakStep(n); };
  const play = () => speakStep(idx);
  const pause = () => { window.speechSynthesis?.cancel(); setPlaying(false); };

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Mic not supported in this browser"); return; }
    const rec = new SR();
    rec.lang = "en-US"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = async (ev) => {
      const q = ev.results[0][0].transcript;
      setTranscript(q); setListening(false); setAnswerLoading(true);
      try {
        const r = await askCoach(q, id, steps[idx]?.idx);
        setAnswer(r.answer || "");
        if (!muted) speak(r.answer || "");
      } catch (e) { toast.error("Coach failed"); }
      finally { setAnswerLoading(false); }
    };
    rec.onerror = () => { setListening(false); toast.error("Mic error"); };
    rec.onend = () => setListening(false);
    recogRef.current = rec;
    rec.start(); setListening(true);
  };
  const stopListening = () => { recogRef.current?.stop(); setListening(false); };

  // Wake-word: continuous SR listening for "hey chef" — then trigger startListening
  const startWake = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice not supported in this browser"); return; }
    const rec = new SR();
    rec.lang = "en-US"; rec.continuous = true; rec.interimResults = true;
    rec.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript.toLowerCase().trim();
        if (/\bhey,?\s*chef\b/.test(t) || /\bok,?\s*chef\b/.test(t)) {
          try { rec.stop(); } catch (_) {}
          setWakeActive(false);
          if (!muted) speak("Yes?");
          setTimeout(() => startListening(), 500);
          return;
        }
      }
    };
    rec.onerror = () => setWakeActive(false);
    rec.onend = () => { /* if still active, restart */
      if (wakeRef.current === rec && wakeActive) {
        try { rec.start(); } catch (_) {}
      }
    };
    wakeRef.current = rec;
    try { rec.start(); setWakeActive(true); toast.success('Listening for "hey chef"'); }
    catch (e) { toast.error("Cannot start wake listener"); }
  };
  const stopWake = () => {
    if (wakeRef.current) { try { wakeRef.current.stop(); } catch (_) {} wakeRef.current = null; }
    setWakeActive(false);
  };

  const askText = async (q) => {
    if (!q.trim()) return;
    setAnswerLoading(true); setTranscript(q);
    try {
      const r = await askCoach(q, id, steps[idx]?.idx);
      setAnswer(r.answer || "");
      if (!muted) speak(r.answer || "");
    } catch (e) { toast.error("Coach failed"); }
    finally { setAnswerLoading(false); }
  };

  if (loading) return <div className="mx-auto max-w-[900px] px-6 py-16 font-mono text-muted-foreground">Preparing steps…</div>;
  if (!recipe) return null;

  const cur = steps[idx];

  return (
    <div className="mx-auto max-w-[900px] px-6 lg:px-10 py-8 space-y-6" data-testid="cook-page">
      <div className="pb-4 border-b border-border">
        <Link to={`/recipe/${id}`} data-testid="back-recipe" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
          <ArrowLeft size={14} /> Back to recipe
        </Link>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Voice cook mode</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-2">{recipe.name}</h1>
      </div>

      {steps.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center font-mono text-sm text-muted-foreground" data-testid="no-steps">
          No steps generated. Try opening the recipe first, then reload this page.
        </div>
      ) : (
        <>
          {/* Big current step */}
          <div className="border border-border bg-card p-8 text-center min-h-[280px] flex flex-col justify-center" data-testid="current-step">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Step {idx + 1} of {steps.length}
            </div>
            <div className="mt-4 font-display font-medium text-2xl md:text-3xl leading-tight" data-testid="step-text">
              {cur?.text}
            </div>
            {cur?.duration_sec > 0 && (
              <div className="mt-4 border border-border bg-background p-4 flex items-center justify-center gap-4" data-testid="timer">
                <Timer size={20} className="text-primary" />
                <div className="font-mono text-4xl font-medium tracking-tight tabular-nums" data-testid="timer-value">
                  {String(Math.floor((timerLeft || cur.duration_sec) / 60)).padStart(2, "0")}:{String((timerLeft || cur.duration_sec) % 60).padStart(2, "0")}
                </div>
                {timerRunning ? (
                  <button onClick={pauseTimer} data-testid="timer-pause"
                    className="h-8 px-3 border border-border hover:bg-muted font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">Pause</button>
                ) : (
                  <button onClick={startTimer} data-testid="timer-start"
                    className="h-8 px-3 border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">Start</button>
                )}
                <button onClick={resetTimer} data-testid="timer-reset"
                  className="h-8 px-3 border border-border hover:bg-muted font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">Reset</button>
                <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground cursor-pointer" data-testid="auto-adv-label">
                  <input type="checkbox" checked={autoAdvance} onChange={e => setAutoAdvance(e.target.checked)}
                    data-testid="auto-adv-toggle" className="accent-primary" />
                  Auto-advance
                </label>
              </div>
            )}
          </div>

          {/* Transport */}
          <div className="grid grid-cols-4 gap-px bg-border border border-border" data-testid="transport">
            <button onClick={prev} disabled={idx === 0} data-testid="prev-step"
              className="bg-card p-4 hover:bg-muted disabled:opacity-40 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
              <SkipBack size={18} /> Prev
            </button>
            {playing ? (
              <button onClick={pause} data-testid="pause"
                className="bg-primary text-primary-foreground p-4 hover:bg-primary/90 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
                <Pause size={18} weight="fill" /> Pause
              </button>
            ) : (
              <button onClick={play} data-testid="play"
                className="bg-primary text-primary-foreground p-4 hover:bg-primary/90 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
                <Play size={18} weight="fill" /> Speak
              </button>
            )}
            <button onClick={next} disabled={idx === steps.length - 1} data-testid="next-step"
              className="bg-card p-4 hover:bg-muted disabled:opacity-40 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
              Next <SkipForward size={18} />
            </button>
            <button onClick={() => setMuted(m => !m)} data-testid="mute"
              className="bg-card p-4 hover:bg-muted flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
              {muted ? <><SpeakerSlash size={18} /> Muted</> : <><SpeakerHigh size={18} /> Sound</>}
            </button>
          </div>

          {/* Coach */}
          <div className="border border-border" data-testid="coach-panel">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-1.5">
                <ChatCircle size={12} weight="fill" className="text-primary" /> Ask the coach
              </div>
              {listening ? (
                <button onClick={stopListening} data-testid="mic-stop"
                  className="h-9 px-3 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 animate-pulse transition-colors duration-200">
                  <MicrophoneSlash size={14} /> Stop
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button onClick={wakeActive ? stopWake : startWake} data-testid="wake-toggle"
                    className={`h-9 px-3 border font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200 ${
                      wakeActive ? "border-accent text-accent hover:bg-accent hover:text-accent-foreground" : "border-border hover:bg-muted"
                    }`}>
                    <Ear size={14} weight={wakeActive ? "fill" : "regular"} /> {wakeActive ? "\"Hey chef\" ON" : "\"Hey chef\""}
                  </button>
                  <button onClick={startListening} data-testid="mic-start"
                    className="h-9 px-3 border border-border hover:bg-muted font-mono text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200">
                    <Microphone size={14} /> Ask
                  </button>
                </div>
              )}
            </div>
            <div className="p-4 space-y-3">
              <div className="flex flex-wrap gap-1.5" data-testid="quick-qs">
                {["Can I use olive oil instead?", "How hot should the pan be?", "How do I know it's done?"].map(q => (
                  <button key={q} onClick={() => askText(q)}
                    className="h-8 px-3 border border-border hover:bg-muted font-mono text-[11px] transition-colors duration-200">{q}</button>
                ))}
              </div>
              {transcript && (
                <div className="border border-border p-3" data-testid="transcript">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">You asked</div>
                  <div className="mt-1 italic">{transcript}</div>
                </div>
              )}
              {answerLoading && <div className="font-mono text-xs text-muted-foreground">Coach is thinking…</div>}
              {answer && (
                <div className="border border-primary/50 bg-primary/5 p-3" data-testid="coach-answer">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Coach</div>
                  <div className="mt-1 leading-relaxed">{answer}</div>
                </div>
              )}
            </div>
          </div>

          {/* All steps list */}
          <div className="border border-border" data-testid="step-list">
            {steps.map((s, i) => (
              <button key={s.idx} onClick={() => { setIdx(i); speakStep(i); }}
                data-testid={`goto-${i}`}
                className={`w-full text-left grid grid-cols-[36px_1fr] gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted transition-colors duration-200 ${idx === i ? "bg-muted/50" : ""}`}>
                <span className="font-mono text-sm text-muted-foreground">{i + 1}.</span>
                <span className="text-sm">{s.text}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
